import { useMemo, useState } from 'react'
import { Edit, Plus, Wrench } from '@/ui/icons'
import { Button, Card, CardContent, Skeleton } from '@/ui/components'
import { EmptyState } from '@/ui/design-system'
import type { ModificationCategory, VehicleModification } from '@/shared/api'
import { useVehicleModifications, useVehicleModificationStats } from '../hooks/useVehicleModifications'
import { AddModificationModal } from './AddModificationModal'

const CATEGORY_LABELS: Record<ModificationCategory, string> = {
  armor: 'Armor',
  suspension: 'Suspension',
  recovery: 'Recovery',
  lighting: 'Lighting',
  storage: 'Storage',
  electrical: 'Electrical',
  tires_wheels: 'Tires and wheels',
  roof_rack: 'Roof rack',
  roof_top_tent: 'Roof top tent',
  truck_camper: 'Truck camper',
  other: 'Other',
}

const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS) as ModificationCategory[]

function formatDollars(amount: number): string {
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

interface VehicleModificationsProfileProps {
  vehicleId: string
  /** True only for the vehicle's owner: shows the add and edit controls and prices. */
  canEdit: boolean
}

export function VehicleModificationsProfile({ vehicleId, canEdit }: VehicleModificationsProfileProps) {
  const { modifications, isLoading, error, refetch } = useVehicleModifications(vehicleId)
  const { stats } = useVehicleModificationStats(vehicleId)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingModification, setEditingModification] = useState<VehicleModification | null>(null)

  const groups = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        items: modifications.filter((mod) => mod.category === category),
      })).filter((group) => group.items.length > 0),
    [modifications]
  )

  const openAdd = () => {
    setEditingModification(null)
    setIsModalOpen(true)
  }

  const openEdit = (modification: VehicleModification) => {
    setEditingModification(modification)
    setIsModalOpen(true)
  }

  const summary = [
    `${modifications.length} ${modifications.length === 1 ? 'mod' : 'mods'} in ${groups.length} ${groups.length === 1 ? 'category' : 'categories'}`,
    canEdit && stats?.totalInvestment ? `${formatDollars(stats.totalInvestment)} invested` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Card className="border-border bg-card shadow-none">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">Build list</h2>
            {!isLoading && !error && modifications.length > 0 && (
              <p className="mt-0.5 text-sm text-earth-600">{summary}</p>
            )}
          </div>
          {canEdit && modifications.length > 0 && (
            <Button variant="outline" size="sm" onClick={openAdd} className="flex-shrink-0 gap-1.5">
              <Plus className="h-4 w-4" aria-hidden />
              Add mod
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="mt-5 space-y-3" aria-hidden>
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : error ? (
          <div className="mt-5" role="alert">
            <p className="text-sm text-foreground">The build list didn't load.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : modifications.length === 0 ? (
          canEdit ? (
            <EmptyState
              className="py-8"
              icon={<Wrench className="mx-auto h-6 w-6 text-earth-500" aria-hidden />}
              title="No mods yet"
              description="List what you've fitted so your crew knows what this rig can do."
              action={{ label: 'Add your first mod', onClick: openAdd }}
            />
          ) : (
            <p className="mt-3 text-sm text-earth-600">No mods listed yet.</p>
          )
        ) : (
          <div className="mt-5 space-y-6">
            {groups.map(({ category, items }) => (
              <section key={category}>
                <h3 className="text-sm font-medium text-earth-600">{CATEGORY_LABELS[category]}</h3>
                <ul className="mt-1 divide-y divide-border">
                  {items.map((mod) => {
                    const brand = mod.brand_name || mod.custom_brand_name
                    const detail = [brand, mod.product_model].filter(Boolean).join(' · ')
                    return (
                      <li key={mod.id} className="group flex items-start gap-3 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="break-words text-sm font-medium text-foreground">{mod.product_name}</p>
                          {detail && <p className="mt-0.5 break-words text-sm text-earth-600">{detail}</p>}
                          {mod.description && (
                            <p className="mt-1 line-clamp-2 break-words text-sm text-earth-600">{mod.description}</p>
                          )}
                        </div>
                        {canEdit && !!mod.purchase_price && (
                          <span className="flex-shrink-0 text-sm tabular-nums text-foreground">
                            {formatDollars(mod.purchase_price)}
                          </span>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="-my-1 h-7 w-7 flex-shrink-0 p-0 text-earth-600 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:opacity-0"
                            onClick={() => openEdit(mod)}
                            aria-label={`Edit ${mod.product_name}`}
                          >
                            <Edit className="h-4 w-4" aria-hidden />
                          </Button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </CardContent>

      {canEdit && (
        <AddModificationModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setEditingModification(null)
          }}
          vehicleId={vehicleId}
          editingModification={editingModification}
        />
      )}
    </Card>
  )
}
